/**
 * Rule Registry — Single source of truth for all configurable rules,
 * standards, regulations, and material prices.
 *
 * Rule definitions are split into per-category files under ./rules/.
 * This module re-assembles them, provides lookup helpers, and exports
 * the compile-time-safe RuleId union type.
 */

// ─── Types ──────────────────────────────────────────────────

export type RuleCategory =
	| "electrical"
	| "heater"
	| "foundation"
	| "framing"
	| "weight"
	| "dimensions"
	| "ventilation"
	| "glass"
	| "permit"
	| "ergonomics"
	| "snowzone"
	| "exterior"
	| "bbr_text"
	| "prices";

export type DangerLevel = "info" | "warning" | "critical";

export type RuleValueType =
	| "number"
	| "string"
	| "boolean"
	| "number[]"
	| "json";

export interface RuleDefinition {
	id: string;
	category: RuleCategory;
	label: string;
	description: string;
	defaultValue: unknown;
	valueType: RuleValueType;
	unit?: string;
	source?: string;
	dangerLevel?: DangerLevel;
}

/** Category metadata for admin UI grouping */
export interface CategoryMeta {
	id: RuleCategory;
	label: string;
	icon: string;
	description: string;
}

export const CATEGORIES: CategoryMeta[] = [
	{
		id: "electrical",
		label: "Elkablar & Zoner",
		icon: "⚡",
		description: "Zon-indelning, kabeltyper, SEK Svensk Elstandard",
	},
	{
		id: "heater",
		label: "Aggregat & Effekt",
		icon: "🔥",
		description: "Bastuaggregat, effektberäkning, säkringar",
	},
	{
		id: "foundation",
		label: "Grund & Plintar",
		icon: "🏗️",
		description: "Bärlinor, plintavstånd, strukturella regler",
	},
	{
		id: "framing",
		label: "Stomme & Reglar",
		icon: "🪵",
		description: "c/c-avstånd, regelvirke, väggstomme",
	},
	{
		id: "weight",
		label: "Vikt & Transport",
		icon: "⚖️",
		description: "Densiteter, komponentvikter, transportgränser",
	},
	{
		id: "dimensions",
		label: "Bastumått",
		icon: "📐",
		description: "Takhöjd, virkesdimensioner",
	},
	{
		id: "ventilation",
		label: "Ventilation",
		icon: "💨",
		description: "Tillufts- och frånluftsplacering, finsk/svensk standard",
	},
	{
		id: "glass",
		label: "Glas & Panoramaväggar",
		icon: "🪟",
		description: "Panoramaglas, säkerhetsglas och energiantaganden",
	},
	{
		id: "permit",
		label: "Bygglov & Komplementbyggnad",
		icon: "🏛️",
		description:
			"PBL-trösklar för bygglovsbefriad komplementbyggnad (reform 1 dec 2025)",
	},
	{
		id: "ergonomics",
		label: "Lav-ergonomi",
		icon: "🧖",
		description: "Lavhöjder, Löylyns lag, kallras-validering",
	},
	{
		id: "snowzone",
		label: "Snözon & Takbjälkar",
		icon: "❄️",
		description: "Postnummer → snözon, takbjälkedimensionering",
	},
	{
		id: "exterior",
		label: "Utvändig Fasad",
		icon: "🏡",
		description: "Ytterpanel, spikläkt, vindskydd, takfall (AMA Hus)",
	},
	{
		id: "bbr_text",
		label: "BBR & Regeltexter",
		icon: "📜",
		description: "Boverkets byggregler, sotarkrav, ventilation",
	},
	{
		id: "prices",
		label: "Materialpriser",
		icon: "💰",
		description: "Bygghändel-priser inkl. moms (SEK)",
	},
];

// ─── Assembled Rule Definitions ─────────────────────────────

import { ALL_RULES, type RuleId } from "./ruleIds";

export type { RuleId };

/** Flat array of every rule definition (backward-compatible export). */
export const RULE_DEFAULTS: RuleDefinition[] = [
	...ALL_RULES,
] as unknown as RuleDefinition[];

// ─── Lookup helpers ─────────────────────────────────────────

/** Map from rule ID to its definition, for O(1) access */
export const RULES_BY_ID = new Map<string, RuleDefinition>(
	RULE_DEFAULTS.map((r) => [r.id, r]),
);

/** Active overrides (loaded from DB at app start) */
export type RuleOverrides = Record<string, unknown>;

/**
 * Get the effective value for a rule: override wins, else default.
 * Accepts any string for backward compatibility, but prefers RuleId.
 */
export function getRuleValue<T = unknown>(
	ruleId: RuleId | (string & {}),
	overrides: RuleOverrides = {},
): T {
	if (ruleId in overrides) {
		return overrides[ruleId] as T;
	}
	const def = RULES_BY_ID.get(ruleId);
	if (!def) {
		throw new Error(`Unknown rule: ${ruleId}`);
	}
	return def.defaultValue as T;
}

/**
 * Get all rules for a category with resolved values.
 */
export function getCategoryRules(
	category: RuleCategory,
	overrides: RuleOverrides = {},
): Array<{ rule: RuleDefinition; value: unknown; isOverridden: boolean }> {
	return RULE_DEFAULTS.flatMap((r) =>
		r.category !== category
			? []
			: [
					{
						rule: r,
						value: getRuleValue(r.id, overrides),
						isOverridden: r.id in overrides,
					},
				],
	);
}
